/**
 * Apple HealthKit React Hook
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { 
  isHealthKitAvailable, 
  requestHealthPermissions, 
  readLatestHealthData,
  checkAuthorizationStatus
} from '../lib/healthkit';
import { illuminateApi } from '../features/illuminate/api';
import type { HealthMetric } from '../features/illuminate/api';

export type AppleHealthConnectionStatus =
  | 'checking'
  | 'unsupported'
  | 'needs_authorization'
  | 'authorized'
  | 'syncing'
  | 'synced'
  | 'error';

export function useAppleHealth() {
  const [isAvailable, setIsAvailable] = useState<boolean>(false);
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [healthData, setHealthData] = useState<HealthMetric[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isSyncingToCloud, setIsSyncingToCloud] = useState<boolean>(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [lastSyncedCount, setLastSyncedCount] = useState<number>(0);

  const performCloudSync = useCallback(async (data: HealthMetric[]) => {
    if (data.length === 0) return;
    setIsSyncingToCloud(true);
    try {
      const result = await illuminateApi.syncAppleHealthMetrics(data);
      setLastSyncedAt(new Date().toISOString());
      setLastSyncedCount(result.syncedCount);
    } catch (e) {
      console.warn('Failed to sync health data to cloud', e);
      setError(e instanceof Error ? e.message : 'Apple Health 同步到健康档案失败');
    } finally {
      setIsSyncingToCloud(false);
    }
  }, []);

  // 初始化检查
  useEffect(() => {
    let mounted = true;
    const init = async () => {
      setLoading(true);
      try {
        const available = await isHealthKitAvailable();
        if (mounted) setIsAvailable(available);

        if (available) {
          const authorized = await checkAuthorizationStatus();
          if (mounted) setIsAuthorized(authorized);

          if (authorized) {
            const data = await readLatestHealthData();
            if (mounted) {
              setHealthData(data);
              void performCloudSync(data);
            }
          }
        }
      } catch (e) {
        if (mounted) setError(e instanceof Error ? e.message : '初始化 HealthKit 失败');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    init();
    return () => { mounted = false; };
  }, [performCloudSync]);

  // 手动请求权限并拉取数据
  const requestPermissionAndFetch = useCallback(async () => {
    if (!isAvailable) return false;
    setLoading(true);
    setError(null);
    try {
      const granted = await requestHealthPermissions();
      setIsAuthorized(granted);

      if (granted) {
        const data = await readLatestHealthData();
        setHealthData(data);
        void performCloudSync(data);
      }
      return granted;
    } catch (e) {
      setError(e instanceof Error ? e.message : '授权失败');
      return false;
    } finally {
      setLoading(false);
    }
  }, [isAvailable, performCloudSync]);

  // 手动刷新数据
  const refresh = useCallback(async () => {
    if (!isAvailable || !isAuthorized) return;
    setLoading(true);
    try {
      const data = await readLatestHealthData();
      setHealthData(data);
      void performCloudSync(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : '刷新数据失败');
    } finally {
      setLoading(false);
    }
  }, [isAvailable, isAuthorized, performCloudSync]);

  const connectionStatus = useMemo<{
    status: AppleHealthConnectionStatus;
    label: string;
    note: string;
    manualFallbackAvailable: boolean;
  }>(() => {
    if (loading) {
      return {
        status: 'checking',
        label: '检查中',
        note: '正在检查本机 Apple Health 支持与授权状态。',
        manualFallbackAvailable: true,
      };
    }
    if (error) {
      return {
        status: 'error',
        label: '同步异常',
        note: error,
        manualFallbackAvailable: true,
      };
    }
    if (!isAvailable) {
      return {
        status: 'unsupported',
        label: '本机不可用',
        note: '当前环境无法直接读取 Apple Health；可以继续用手动记录完成闭环。',
        manualFallbackAvailable: true,
      };
    }
    if (!isAuthorized) {
      return {
        status: 'needs_authorization',
        label: '待授权',
        note: '授权后会读取步数、心率、体重、睡眠、活动日汇总和单次运动摘要，并同步到健康档案。',
        manualFallbackAvailable: true,
      };
    }
    if (isSyncingToCloud) {
      return {
        status: 'syncing',
        label: '同步中',
        note: '正在把步数、心率、体重、睡眠、活动日汇总和单次运动摘要同步到健康档案。',
        manualFallbackAvailable: true,
      };
    }
    if (lastSyncedAt) {
      return {
        status: 'synced',
        label: '已同步',
        note: `已同步 ${lastSyncedCount} 条样本到健康档案。`,
        manualFallbackAvailable: true,
      };
    }
    return {
      status: 'authorized',
      label: '已授权',
      note: healthData.length > 0 ? '已读取步数、心率、体重、睡眠、活动日汇总和单次运动摘要，等待云端确认。' : '已授权，但最近没有读到可同步样本。',
      manualFallbackAvailable: true,
    };
  }, [error, healthData.length, isAuthorized, isAvailable, isSyncingToCloud, lastSyncedAt, lastSyncedCount, loading]);

  return {
    isAvailable,
    isAuthorized,
    healthData,
    loading,
    error,
    isSyncingToCloud,
    lastSyncedAt,
    lastSyncedCount,
    connectionStatus,
    requestPermission: requestPermissionAndFetch,
    refresh,
  };
}
